/**
 * Searchable single-country picker.
 * Reuses the existing COUNTRIES list from lib/satisfinderCountries so
 * we don't ship a second country dataset. Shows each country's flag
 * emoji + name. Selection returns the country NAME (matches the
 * backend region resolver which accepts either name or ISO-2).
 *
 * Design notes:
 *   • Non-searchable dropdowns are painful for a 200-country list.
 *   • This uses shadcn Popover + Command for filtering.
 *   • No external calls — data is bundled.
 */
import React, { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { COUNTRIES, iso2ToFlag } from '@/lib/satisfinderCountries';

export default function CountryCombobox({
  value,
  onChange,
  placeholder = 'Select a country…',
  testId = 'country-combobox',
}) {
  const [open, setOpen] = useState(false);
  const selected = COUNTRIES.find(c => c.name === value || c.iso === (value || '').toUpperCase()) || null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between text-left font-normal"
          data-testid={`${testId}-trigger`}
        >
          {selected ? (
            <span className="flex items-center gap-2 truncate">
              <span aria-hidden>{iso2ToFlag(selected.iso)}</span>
              <span className="truncate">{selected.name}</span>
            </span>
          ) : (
            <span className="text-[#64748B]">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter>
          <CommandInput placeholder="Search country…" data-testid={`${testId}-input`} />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {COUNTRIES.map(c => (
                <CommandItem
                  key={c.iso}
                  value={c.name}
                  onSelect={() => { onChange(c.name); setOpen(false); }}
                  data-testid={`${testId}-item-${c.iso}`}
                >
                  <span className="mr-2" aria-hidden>{iso2ToFlag(c.iso)}</span>
                  <span className="truncate">{c.name}</span>
                  <Check className={cn('ml-auto h-4 w-4', selected?.iso === c.iso ? 'opacity-100' : 'opacity-0')} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
